import { Button, FormControl, TextInput } from "@biom3/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
} from "react";
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from "react-hook-form";
import { z } from "zod";

import { StatefulBox } from "@/components";
import { Modal as ModalBase } from "@/components/modal";
import { useNameWallet } from "@/hooks";
import type { Wallet } from "@/types";
import { analytics } from "@analytics";

type NameWalletModalProps = {
  linkedWallets: Wallet[] | undefined;
  wallet: Wallet | null;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
};

const NAME = "name";

const Modal = ({
  wallet,
  isModalOpen,
  setIsModalOpen,
}: NameWalletModalProps) => {
  const { control, formState, handleSubmit, setValue, reset } =
    useFormContext();
  const { field: nameField } = useController({ name: NAME, control });

  const closeModal = useCallback(async () => {
    setIsModalOpen(false);
    reset();
  }, [reset, setIsModalOpen]);

  const {
    mutate: editName,
    isPending,
    isSuccess,
    isError,
    error,
    reset: mutateReset,
  } = useNameWallet();

  useEffect(() => {
    if (isSuccess || isError) {
      closeModal();
    }
    if (isError) {
      console.log(error);
    }
  }, [isSuccess, isError, closeModal, error]);

  const onNameWallet = () => {
    analytics.track("PassportDashboardWalletsNameWallet_ClickButtonRequest");
    mutateReset();
    const req = {
      address: wallet?.address,
      name: nameField.value,
    };
    editName(req);
  };

  return (
    <ModalBase open={isModalOpen} title="Update wallet name">
      <ModalBase.Content>
        <ModalBase.ContentLayout>
          <StatefulBox isLoading={isPending} error={error}>
            <FormControl
              validationStatus={
                formState.errors?.name?.message ? "error" : undefined
              }
            >
              <FormControl.Label>Wallet name</FormControl.Label>
              <TextInput
                onChange={nameField.onChange}
                onBlur={nameField.onBlur}
                value={nameField.value}
                name={nameField.name}
                inputRef={nameField.ref}
                placeholder="e.g. My game wallet"
                hideClearValueButton={false}
                onClearValue={() => setValue(nameField.name, "")}
              />
              {formState.errors?.name?.message && (
                <FormControl.Validation>
                  {formState.errors?.name?.message as string}
                </FormControl.Validation>
              )}
              <FormControl.Caption>Maximum 40 characters</FormControl.Caption>
            </FormControl>
          </StatefulBox>
        </ModalBase.ContentLayout>
      </ModalBase.Content>
      <ModalBase.Actions>
        <Button
          variant="secondary"
          disabled={isPending}
          onClick={() => {
            analytics.track(
              "PassportDashboardWalletsCloseNameWalletModal_ClickButton",
            );
            closeModal();
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isPending || isError}
          onClick={handleSubmit(onNameWallet)}
          sx={{ ml: "base.spacing.x2", minWidth: "fit-content" }}
        >
          Save
        </Button>
      </ModalBase.Actions>
    </ModalBase>
  );
};

export const NameWalletModal = ({
  linkedWallets,
  wallet,
  isModalOpen,
  setIsModalOpen,
}: NameWalletModalProps) => {
  const Schema = z.object({
    name: z
      .string()
      .min(1, { message: "Wallet name cannot be empty" })
      .max(40, { message: "Wallet name cannot exceed 40 characters" })
      .refine(
        (value) =>
          !linkedWallets?.find(
            (w) => w.address !== wallet?.address && w.name === value,
          ),
        {
          message: "You've already got a wallet with this name",
        },
      ),
  });

  const methods = useForm<z.infer<typeof Schema>>({
    resolver: zodResolver(Schema),
  });

  const { setValue } = methods;

  useEffect(() => {
    isModalOpen && setValue(NAME, wallet?.name ? wallet?.name : "");
  }, [isModalOpen, setValue, wallet?.name]);

  return (
    <FormProvider {...methods}>
      <Modal
        linkedWallets={linkedWallets}
        wallet={wallet}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </FormProvider>
  );
};
